import { Fragment, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Crosshair, ExternalLink, HelpCircle, Loader2, MapPinned, Search } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { canAccessContractorsPanel } from "@/features/contractors/utils";
import { WORK_TYPE_BADGE } from "@/features/contractors/workTypes";
import {
  AREA_MAP_KINDS,
  KIND_LABELS,
  ORDER_GROUP_LABELS,
  ORDER_GROUPS,
  useContractorAreaMap,
  useContractorAreaMapBases,
  useContractorAreaMapProjects,
  useCountyBoundaries,
  type AreaMapContractor,
  type AreaMapKind,
  type OrderCounty,
} from "../area_map/api";
import {
  ContractorAreaCanvas,
  EMPTY_PIN_COLOR,
  PinGlyph,
  type AreaMapLayers,
} from "../area_map/ContractorAreaCanvas";
import { contractorColor } from "../area_map/geometry";
import {
  areaSummary,
  filterContractors,
  visibleContractorIds,
  type KindFilter,
} from "../area_map/selection";

/** Filtry rodzaju = typy prac z kartoteki (projektant sprawdzający nie trafia na mapę wcale —
 * sprawdza dokumenty na całą Polskę). Można zaznaczyć kilka naraz; „Wszyscy" czyści wybór
 * i pokazuje wszystkie aktywne firmy, także bez typu. */
const KIND_FILTERS: { value: "" | AreaMapKind; label: string }[] = [
  { value: "", label: "Wszyscy" },
  { value: "wpw", label: "WPW" },
  { value: "surveyor", label: "Geodeci" },
  { value: "surveyor_wpw", label: "Geodeci z WPW" },
  { value: "coordinator", label: "Koordynatorzy" },
];

const KIND_BADGE: Record<AreaMapKind, string> = WORK_TYPE_BADGE;

const LAYER_LABELS: { key: keyof AreaMapLayers; label: string }[] = [
  { key: "fills", label: "Wypełnienie obszarów" },
  { key: "outsideOrders", label: "Przyjęte zamówienia poza obszarem (linia przerywana)" },
  { key: "projects", label: "Projekty (pinezki w kolorach firm)" },
  { key: "countyLines", label: "Granice powiatów" },
  { key: "voivodeshipLines", label: "Granice województw" },
  { key: "bases", label: "Siedziby firm" },
];

/** Podwarstwy pinezek: stan zamówień pokazanych firm w projekcie (kolor pinezki = firmy). */
const PIN_SUBLAYERS: { key: keyof AreaMapLayers; label: string; grey?: boolean }[] = [
  { key: "pinsAccepted", label: "przyjęte" },
  { key: "pinsPending", label: "zlecone, nieprzyjęte" },
  { key: "pinsMixed", label: "jedne i drugie" },
  { key: "pinsEmpty", label: "aktywne bez zamówień (szare)", grey: true },
];

/** Dymek nad wierszem: obszar działania i typy prac z kartoteki (one decydują o grupie). */
function rowHint(c: AreaMapContractor): string {
  return [
    `Obszar działania: ${areaSummary(c.areas)}`,
    `Typy prac (kartoteka): ${c.kind_labels.length ? c.kind_labels.join(", ") : "brak"}`,
  ].join("\n");
}

/** Dymek powiatu — nazwy powiatów powtarzają się między województwami (np. „średzki”). */
function orderCountyHint(county: OrderCounty): string {
  if (!county.teryt) return "Zamówienie bez działek z kodem TERYT";
  const unit = county.name.startsWith("m. ") ? county.name : `pow. ${county.name}`;
  return `${unit}, woj. ${county.voivodeship_label}`;
}

/** Powiaty aktywnych zamówień pod nazwą firmy — WPW i mapy osobno. */
function ActiveOrderCounties({ contractor }: { contractor: AreaMapContractor }) {
  const groups = ORDER_GROUPS.filter((g) => contractor.orders[g]);
  if (groups.length === 0) {
    return <span className="block text-[11px] italic text-gray-400">brak aktywnych zamówień</span>;
  }
  return (
    <>
      {groups.map((g) => {
        const summary = contractor.orders[g]!;
        return (
          <span key={g} className="block text-[11px] leading-snug text-gray-600 dark:text-gray-400">
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              {ORDER_GROUP_LABELS[g]} · {summary.count} zam.:
            </span>{" "}
            {summary.counties.map((county, i) => (
              <Fragment key={county.teryt || "unknown"}>
                {i > 0 && ", "}
                {/* Line breaks only after a comma — never between a county and its count. */}
                <span className="whitespace-nowrap" title={orderCountyHint(county)}>
                  {county.name} ({county.count})
                </span>
              </Fragment>
            ))}
          </span>
        );
      })}
    </>
  );
}

export function ContractorAreaMapPage() {
  const contractorsQuery = useContractorAreaMap();
  const countiesQuery = useCountyBoundaries();
  const basesQuery = useContractorAreaMapBases();
  const projectsQuery = useContractorAreaMapProjects();

  const contractors = useMemo(() => contractorsQuery.data ?? [], [contractorsQuery.data]);
  const counties = useMemo(() => countiesQuery.data ?? [], [countiesQuery.data]);
  const bases = useMemo(() => basesQuery.data ?? [], [basesQuery.data]);
  const projects = useMemo(() => projectsQuery.data?.projects ?? [], [projectsQuery.data]);

  const [kind, setKind] = useState<KindFilter>([]);
  const toggleKind = (value: "" | AreaMapKind) =>
    setKind((prev) =>
      value === "" ? [] : prev.includes(value) ? prev.filter((k) => k !== value) : [...prev, value],
    );
  const [query, setQuery] = useState("");
  // Unticked firms — empty = everyone shown (the default), and a firm that appears later
  // starts ticked.
  const [excluded, setExcluded] = useState<Set<string>>(() => new Set());
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ id: string; key: number } | null>(null);
  // Rozwinięte „Informacje dla koordynatorów" (ikona „?") — jedna firma naraz.
  const [infoOpenId, setInfoOpenId] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  // Deweloper widzi SAMĄ megamapę — link do karty firmy odbiłby go na pulpit, więc go nie ma.
  const canOpenCard = canAccessContractorsPanel(user);
  const [layers, setLayers] = useState<AreaMapLayers>({
    fills: true,
    countyLines: true,
    voivodeshipLines: true,
    bases: true,
    outsideOrders: true,
    projects: true,
    pinsAccepted: true,
    pinsPending: true,
    pinsMixed: true,
    pinsEmpty: true,
  });

  // Colour by position on the FULL list, so it stays put while filtering.
  const colors = useMemo(
    () => new Map(contractors.map((c, i) => [c.id, contractorColor(i)])),
    [contractors],
  );
  const listed = useMemo(() => filterContractors(contractors, kind, query), [contractors, kind, query]);
  const visibleIds = useMemo(
    () => visibleContractorIds(contractors, kind, excluded),
    [contractors, kind, excluded],
  );
  const kindCounts = useMemo(
    () => ({
      "": contractors.length,
      ...Object.fromEntries(
        AREA_MAP_KINDS.map((k) => [k, contractors.filter((c) => c.kinds.includes(k)).length]),
      ),
    }) as Record<"" | AreaMapKind, number>,
    [contractors],
  );
  const withoutArea = contractors.filter((c) => c.county_codes.length === 0).length;
  const untyped = contractors.filter((c) => c.kinds.length === 0).length;
  const listedShown = listed.filter((c) => !excluded.has(c.id)).length;

  const toggle = (id: string) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  // „Zaznacz / odznacz wszystkie” działa na to, co widać na liście (po filtrze i szukajce).
  const setListed = (show: boolean) =>
    setExcluded((prev) => {
      const next = new Set(prev);
      for (const c of listed) {
        if (show) next.delete(c.id);
        else next.add(c.id);
      }
      return next;
    });

  if (contractorsQuery.isLoading || countiesQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-gray-400">
        <Loader2 className="mr-2 animate-spin" size={18} /> Ładowanie megamapy podwykonawców…
      </div>
    );
  }
  if (contractorsQuery.isError || countiesQuery.isError) {
    return (
      <div className="flex h-full items-center justify-center text-red-500">
        Nie udało się wczytać danych megamapy podwykonawców.
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-gray-200 px-4 py-2 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <MapPinned size={18} className="text-red-600" />
          <h1 className="text-base font-semibold">Megamapa podwykonawców</h1>
        </div>
        <span className="text-xs text-gray-500">
          {/* "Label: n" — no Polish plural forms to get wrong ("1 koordynatorów"). */}
          Firmy: {contractors.length} · WPW: {kindCounts.wpw} · geodeci: {kindCounts.surveyor} ·
          koordynatorzy: {kindCounts.coordinator}
          {untyped > 0 ? ` · bez typu: ${untyped}` : ""}
          {withoutArea > 0 ? ` · bez obszaru działania: ${withoutArea}` : ""}
          {projectsQuery.data
            ? ` · projekty na mapie: ${projectsQuery.data.projects.length}` +
              (projectsQuery.data.unlocated ? ` (bez lokalizacji: ${projectsQuery.data.unlocated})` : "")
            : ""}
          {basesQuery.isLoading ? " · szukam siedzib…" : ""}
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row">
        <div className="h-[55vh] min-h-[360px] min-w-0 lg:h-auto lg:flex-1">
          <ContractorAreaCanvas
            counties={counties}
            contractors={contractors}
            visibleIds={visibleIds}
            colors={colors}
            bases={bases}
            projects={projects}
            kind={kind}
            layers={layers}
            highlightId={highlightId}
            focus={focus}
          />
        </div>

        <aside className="flex min-h-0 flex-col border-t border-gray-200 bg-gray-50 lg:w-80 lg:shrink-0 lg:border-l lg:border-t-0 dark:border-gray-700 dark:bg-gray-900">
          <div className="space-y-2 border-b border-gray-200 p-3 dark:border-gray-700">
            {/* 3+2: pięć filtrów nie mieści się w jednym rzędzie panelu 20rem. */}
            <div
              className="grid grid-cols-3 gap-0.5 rounded-md border border-gray-300 p-0.5 dark:border-gray-600"
              role="group"
              aria-label="Rodzaj firmy (można zaznaczyć kilka)"
              title="Można zaznaczyć kilka rodzajów naraz — firma wchodzi, gdy ma którykolwiek z nich"
            >
              {KIND_FILTERS.map((f) => {
                const active = f.value === "" ? kind.length === 0 : kind.includes(f.value);
                return (
                  <button
                    key={f.value || "all"}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleKind(f.value)}
                    className={`rounded px-2 py-1 text-xs font-medium transition-colors ${
                      active
                        ? "bg-red-600 text-white"
                        : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                    }`}
                  >
                    {f.label} <span className="opacity-70">({kindCounts[f.value]})</span>
                  </button>
                );
              })}
            </div>
            <div className="relative">
              <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Szukaj firmy, województwa lub powiatu…"
                className="w-full rounded-md border border-gray-300 bg-white py-1.5 pl-7 pr-2 text-xs dark:border-gray-600 dark:bg-gray-800"
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-gray-500">
              <span>
                Na mapie {listedShown} z {listed.length}
              </span>
              <span className="space-x-2">
                <button type="button" onClick={() => setListed(true)} className="hover:text-gray-800 hover:underline dark:hover:text-gray-200">
                  zaznacz wszystkie
                </button>
                <button type="button" onClick={() => setListed(false)} className="hover:text-gray-800 hover:underline dark:hover:text-gray-200">
                  odznacz
                </button>
              </span>
            </div>
          </div>

          <ul className="min-h-[160px] flex-1 space-y-0.5 overflow-y-auto p-2">
            {listed.length === 0 && (
              <li className="px-2 py-4 text-center text-xs text-gray-500">
                {contractors.length === 0
                  ? "Żadna aktywna firma nie ma w umowie ramowej pozycji WPW, geodezyjnych ani koordynacji projektu."
                  : "Brak firm pasujących do filtra."}
              </li>
            )}
            {listed.map((c) => {
              const shown = !excluded.has(c.id);
              return (
                <Fragment key={c.id}>
                <li
                  onMouseEnter={() => shown && setHighlightId(c.id)}
                  onMouseLeave={() => setHighlightId(null)}
                  className="group flex items-start gap-2 rounded px-1.5 py-1 hover:bg-white dark:hover:bg-gray-800"
                  title={rowHint(c)}
                >
                  <input
                    type="checkbox"
                    checked={shown}
                    onChange={() => toggle(c.id)}
                    className="mt-0.5"
                    aria-label={`Pokaż ${c.name} na mapie`}
                  />
                  <span
                    className="mt-0.5 h-3 w-3 shrink-0 rounded-sm"
                    style={{ background: colors.get(c.id), opacity: shown ? 1 : 0.35 }}
                  />
                  <button
                    type="button"
                    onClick={() => toggle(c.id)}
                    className="min-w-0 flex-1 text-left"
                  >
                    <span className="flex flex-wrap items-center gap-1">
                      <span className={`text-xs font-medium ${shown ? "" : "text-gray-400"}`}>{c.name}</span>
                      {c.kinds.map((k) => (
                        <span key={k} className={`rounded px-1 text-[10px] font-medium ${KIND_BADGE[k]}`}>
                          {KIND_LABELS[k]}
                        </span>
                      ))}
                      {c.kinds.length === 0 && (
                        <span
                          className="rounded px-1 text-[10px] font-medium bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                          title="Firma bez typu prac w kartotece — widoczna tylko pod „Wszyscy”"
                        >
                          bez typu
                        </span>
                      )}
                    </span>
                    <ActiveOrderCounties contractor={c} />
                  </button>
                  <span className="flex shrink-0 items-center gap-1 opacity-60 group-hover:opacity-100">
                    {c.coordinator_info.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setInfoOpenId((cur) => (cur === c.id ? null : c.id))}
                        aria-expanded={infoOpenId === c.id}
                        aria-label={`Informacje dla koordynatorów: ${c.name}`}
                        className={`rounded p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 ${
                          infoOpenId === c.id
                            ? "text-sky-700 dark:text-sky-300"
                            : "text-sky-600 hover:text-sky-800 dark:text-sky-400"
                        }`}
                        title="Informacje dla koordynatorów (z umowy ramowej)"
                      >
                        <HelpCircle size={13} />
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => setFocus({ id: c.id, key: Date.now() })}
                      className="rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                      title="Pokaż na mapie"
                    >
                      <Crosshair size={13} />
                    </button>
                    {canOpenCard && (
                      <Link
                        to={`/contractors/${c.id}`}
                        target="_blank"
                        rel="noreferrer"
                        className="rounded p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800 dark:hover:bg-gray-700 dark:hover:text-gray-200"
                        title="Karta podwykonawcy (nowa karta)"
                      >
                        <ExternalLink size={13} />
                      </Link>
                    )}
                  </span>
                </li>
                {infoOpenId === c.id && c.coordinator_info.length > 0 && (
                  <li
                    key={`${c.id}-info`}
                    className="ml-6 mr-1 mb-1 rounded border border-sky-200 bg-sky-50 px-2 py-1.5 text-[11px] text-sky-900 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100"
                    data-testid="contractor-coordinator-info"
                  >
                    <div className="mb-0.5 flex items-center gap-1 font-semibold">
                      <HelpCircle size={11} /> Informacje dla koordynatorów
                    </div>
                    {c.coordinator_info.map((info) => (
                      <p key={info.agreement} className="whitespace-pre-line">
                        {c.coordinator_info.length > 1 && (
                          <span className="text-sky-700 dark:text-sky-300">{info.title}: </span>
                        )}
                        {info.text}
                      </p>
                    ))}
                  </li>
                )}
                </Fragment>
              );
            })}
          </ul>

          <div className="space-y-1 border-t border-gray-200 p-3 dark:border-gray-700">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">Warstwy</div>
            {LAYER_LABELS.map((l) => (
              <Fragment key={l.key}>
                <label className="flex cursor-pointer items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={layers[l.key]}
                    onChange={() => setLayers((prev) => ({ ...prev, [l.key]: !prev[l.key] }))}
                  />
                  {l.label}
                </label>
                {l.key === "projects" && layers.projects && (
                  <div className="space-y-0.5 pb-1 pl-5">
                    {PIN_SUBLAYERS.map((sub) => (
                      <label
                        key={sub.key}
                        className="flex cursor-pointer items-center gap-2 text-[11px] text-gray-600 dark:text-gray-400"
                      >
                        <input
                          type="checkbox"
                          checked={layers[sub.key]}
                          onChange={() =>
                            setLayers((prev) => ({ ...prev, [sub.key]: !prev[sub.key] }))
                          }
                        />
                        {sub.grey && <PinGlyph colors={[EMPTY_PIN_COLOR]} />}
                        {sub.label}
                      </label>
                    ))}
                  </div>
                )}
              </Fragment>
            ))}
            <p className="pt-1 text-[11px] text-gray-500">
              Najedź na powiat, aby zobaczyć, które z pokazanych firm w nim działają i ile mają
              tam aktywnych zamówień; <b className="text-red-600">!</b> = przyjęte zamówienie
              poza obszarem działania. Obszar działania ustawia się na karcie podwykonawcy
              (Zakres współpracy → Obszar działania).
            </p>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default ContractorAreaMapPage;
